import React, { useEffect, useId, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  FieldWrapperVertical,
  SnackbarApiErrorMessage,
  TextArea,
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { IconLibrary } from '@product-framework/index';
import DialogCloseButton from './DialogCloseButton';

function SendMessageDialog({
  open,
  onClose,
  selectedServiceProvider,
  locationId,
}) {
  const { sp } = selectedServiceProvider;
  const spContact = sp?.locations?.find(c => c.id === locationId);
  const notificationEmail = spContact?.dispatchNotificationEmail;
  const primaryContact = spContact?.primaryContact?.email;
  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, post } = useDataProvider(provider);

  const formId = useId();
  const [loading, setLoading] = useState(false);

  const emailOptions = useMemo(() => {
    const seen = new Set();
    return [notificationEmail, primaryContact]
      .map(email => (email || '').trim())
      .filter(Boolean)
      .filter(email => {
        const key = email.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map(email => ({ value: email, label: email }));
  }, [notificationEmail, primaryContact]);

  const schema = yup.object().shape({
    message: yup.string().required(
      formatMessage({
        id: 'MESSAGE_IS_REQUIRED',
        defaultMessage: 'Message is required',
      }),
    ),
    recipientEmails: yup
      .array()
      .of(yup.string().trim().required())
      .min(
        1,
        formatMessage({
          id: 'EMAIL_TO_IS_REQUIRED',
          defaultMessage: 'Email to is required',
        }),
      )
      .required(
        formatMessage({
          id: 'EMAIL_TO_IS_REQUIRED',
          defaultMessage: 'Email to is required',
        }),
      ),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      message: '',
      recipientEmails: notificationEmail ? [notificationEmail.trim()] : [],
    },
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (!open) return;
    reset({
      message: '',
      recipientEmails: notificationEmail ? [notificationEmail.trim()] : [],
    });
  }, [open, notificationEmail, reset]);

  async function onFormSubmit(data) {
    setLoading(true);
    const { recipientEmails, ...rest } = data;
    const body = {
      ...rest,
      recipientEmails,
    };
    const response = await post(
      `${getApiUrl()}/sxcl/v1/email-messages/service-provider/send-message`,
      body,
    );
    if (response.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'SEND_MESASGE_TO_SP_SUCCESS',
          defaultMessage: 'Sent message to service provider successfully',
        }),
      );
      onClose();
    } else {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={response?.errors} />,
      });
    }
    setLoading(false);
  }

  const buttons = [
    {
      children: formatMessage({
        id: 'SEND_MESSAGE',
        defaultMessage: 'Send Message',
      }),
      type: 'submit',
      variant: 'contained',
      color: 'primary',
      startIcon: <IconLibrary.Yes />,
    },
    {
      children: formatMessage({ id: 'CANCEL', defaultMessage: 'Cancel' }),
      onClick: onClose,
      variant: 'contained',
      color: 'secondary',
      startIcon: <IconLibrary.Cancel />,
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconLibrary.Message />
        {formatMessage({
          id: 'SEND_MESSAGE_HEADER',
          defaultMessage: 'Send Message',
        })}
        <DialogCloseButton disabled={loading} onClose={onClose} />
      </DialogTitle>
      <DialogContent>
        <FormProvider {...methods}>
          <form id={formId} noValidate onSubmit={handleSubmit(onFormSubmit)}>
            {loading ? (
              <Box textAlign="center">
                <CircularProgress size="30px" />
                <Typography>Processing</Typography>
              </Box>
            ) : (
              <Grid2>
                <Stack spacing={2}>
                  <Controller
                    name="recipientEmails"
                    control={control}
                    render={({ field, fieldState }) => {
                      const selectedEmails = Array.isArray(field.value)
                        ? field.value
                        : [];
                      return (
                        <FormControl
                          fullWidth
                          error={Boolean(fieldState.error)}>
                          <InputLabel required id="send-message-email-to-label">
                            {formatMessage({
                              id: 'email_recipients',
                              defaultMessage: 'To',
                            })}
                          </InputLabel>
                          <Select
                            size="small"
                            labelId="send-message-email-to-label"
                            multiple
                            inputProps={{ 'aria-required': true }}
                            value={selectedEmails}
                            onChange={e => field.onChange(e.target.value)}
                            onBlur={field.onBlur}
                            inputRef={field.ref}
                            input={
                              <OutlinedInput
                                label={formatMessage({
                                  id: 'EMAIL_TO',
                                  defaultMessage: 'Email to',
                                })}
                              />
                            }
                            renderValue={selected => (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 0.5,
                                }}>
                                {selected.map(value => (
                                  <Chip
                                    key={value}
                                    label={value}
                                    size="small"
                                  />
                                ))}
                              </Box>
                            )}>
                            {emailOptions.map(opt => (
                              <MenuItem key={opt.value} value={opt.value} dense>
                                <Checkbox
                                  size="small"
                                  checked={selectedEmails.includes(opt.value)}
                                  tabIndex={-1}
                                  disableRipple
                                  inputProps={{
                                    'aria-label': opt.label,
                                  }}
                                />
                                <ListItemText
                                  primary={opt.label}
                                  primaryTypographyProps={{
                                    component: 'span',
                                    noWrap: true,
                                  }}
                                />
                              </MenuItem>
                            ))}
                          </Select>
                          {fieldState.error?.message ? (
                            <FormHelperText>
                              {fieldState.error.message}
                            </FormHelperText>
                          ) : null}
                        </FormControl>
                      );
                    }}
                  />
                  <FieldWrapperVertical
                    field={{
                      name: 'message',
                      label: {
                        localeId: 'message',
                        defaultMessage: 'Message',
                      },
                    }}
                    errors={errors}>
                    <TextArea
                      sx={{ width: '100%' }}
                      rows={8}
                      {...methods.register('message')}
                      error={!!errors?.message?.message}
                      helperText={errors?.message?.message || ''}
                    />
                  </FieldWrapperVertical>
                </Stack>
              </Grid2>
            )}
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions display="flex" sx={{ justifyContent: 'center', my: 2 }}>
        {buttons.map(btn => {
          const { children, ...rest } = btn;
          return (
            <Button
              key={children}
              disabled={loading}
              {...rest}
              {...(rest.type === 'submit' ? { form: formId } : {})}>
              {children}
            </Button>
          );
        })}
      </DialogActions>
    </Dialog>
  );
}

export default SendMessageDialog;
